import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  Card, 
  TextField, 
  Stack,
  Container,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  CardContent,
  CardActionArea,
  Chip,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Calendar, FileText, CheckCircle, Building2, User, Search, Code, Shield, Phone, FolderKanban, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Steps dynamiques: Type de projet est toujours premier
const getSteps = (isAdmin) => {
  if (isAdmin) {
    return ['Type', 'Entreprise', 'Description', 'Timeline', 'Récapitulatif'];
  }
  return ['Type', 'Description', 'Timeline', 'Récapitulatif'];
};

function NewSubmission() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const steps = getSteps(isAdmin);
  const [activeStep, setActiveStep] = React.useState(0); // Toujours commencer par la sélection du type
  const [companies, setCompanies] = React.useState([]);
  const [companyUsers, setCompanyUsers] = React.useState([]);
  const [projectTypes, setProjectTypes] = React.useState([]);
  const [formData, setFormData] = React.useState({
    projectTypeId: '',
    companyId: '',
    clientId: '', // Utilisateur contact (optionnel)
    assignLater: false, // Nouveau: créer sans attribution client
    title: '',
    description: '',
    budget: 5000,
    startDate: '',
    endDate: ''
  });
  const navigate = useNavigate();

  // Charger les types de projets
  React.useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        const token = localStorage.getItem('portalToken');
        const response = await fetch('/api/project-types', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setProjectTypes(data);
        }
      } catch (err) {
        console.error('Failed to fetch project types');
      }
    };
    fetchProjectTypes();
  }, []);

  // Icône dynamique pour le type de projet
  const getTypeIcon = (slug, color) => {
    const iconProps = { size: 32, color: color || '#00d4aa' };
    switch (slug) {
      case 'dev': return <Code {...iconProps} />;
      case 'cybersecurity': return <Shield {...iconProps} />;
      case 'voip': return <Phone {...iconProps} />;
      default: return <FolderKanban {...iconProps} />;
    }
  };

  // Charger les entreprises clientes
  React.useEffect(() => {
    if (isAdmin) {
      const fetchCompanies = async () => {
        try {
          const token = localStorage.getItem('portalToken');
          const response = await fetch('/api/companies/clients', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          if (response.ok) {
            setCompanies(data);
          }
        } catch (err) {
          console.error('Failed to fetch companies');
        }
      };
      fetchCompanies();
    }
  }, [isAdmin]);

  // Charger les utilisateurs de l'entreprise sélectionnée
  React.useEffect(() => {
    if (isAdmin && formData.companyId) {
      const fetchCompanyUsers = async () => {
        try {
          const token = localStorage.getItem('portalToken');
          const response = await fetch(`/api/companies/${formData.companyId}/users`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          if (response.ok) {
            setCompanyUsers(data);
            // Auto-sélectionner le premier utilisateur si un seul
            if (data.length === 1) {
              setFormData(prev => ({ ...prev, clientId: data[0].id }));
            } else if (data.length > 0 && !formData.clientId) {
              // Sélectionner le CLIENTADMIN par défaut s'il existe
              const clientAdmin = data.find(u => u.role === 'CLIENTADMIN');
              if (clientAdmin) {
                setFormData(prev => ({ ...prev, clientId: clientAdmin.id }));
              }
            }
          }
        } catch (err) {
          console.error('Failed to fetch company users');
        }
      };
      fetchCompanyUsers();
    } else {
      setCompanyUsers([]);
    }
  }, [isAdmin, formData.companyId]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      
      // Si assignLater est activé, ne pas envoyer de client/entreprise
      let clientId = null;
      let companyId = null;
      
      if (!formData.assignLater) {
        // Déterminer le clientId - utiliser le premier user de l'entreprise si non spécifié
        clientId = formData.clientId;
        if (!clientId && companyUsers.length > 0) {
          clientId = companyUsers[0].id;
        }
        companyId = formData.companyId;
      }

      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          budget: null,
          startDate: formData.startDate,
          endDate: formData.endDate,
          clientId: clientId,
          companyId: companyId,
          projectTypeId: formData.projectTypeId,
          assignLater: formData.assignLater // Nouveau flag
        }),
      });
      if (response.ok) {
        navigate(isAdmin ? '/admin/submissions' : '/portal/submissions');
      }
    } catch (err) {
      console.error('Failed to create submission');
    }
  };

  const selectedCompany = companies.find(c => c.id === formData.companyId);
  const selectedProjectType = projectTypes.find(t => t.id === formData.projectTypeId);

  // Calcul des indices de step selon le mode (admin ou client)
  const getStepIndex = (stepName) => {
    const idx = steps.indexOf(stepName);
    return idx;
  };

  const renderStepContent = (step) => {
    const stepName = steps[step];
    
    // === STEP: TYPE DE PROJET ===
    if (stepName === 'Type') {
      return (
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FolderKanban color="#00d4aa" size={24} />
            <Typography variant="h6">Type de projet</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sélectionnez le type de projet pour ce devis. Cela détermine les catégories et options disponibles.
          </Typography>
          
          <Grid container spacing={2}>
            {projectTypes.map((type) => (
              <Grid item xs={12} sm={6} md={4} key={type.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: formData.projectTypeId === type.id 
                      ? `2px solid ${type.color || '#00d4aa'}` 
                      : '1px solid rgba(255,255,255,0.1)',
                    bgcolor: formData.projectTypeId === type.id 
                      ? `${type.color || '#00d4aa'}10` 
                      : 'rgba(255,255,255,0.02)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: type.color || '#00d4aa',
                      bgcolor: `${type.color || '#00d4aa'}08`
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => setFormData({ ...formData, projectTypeId: type.id })}
                    sx={{ height: '100%', p: 2 }}
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ 
                        mb: 2, 
                        display: 'flex', 
                        justifyContent: 'center',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: `${type.color || '#00d4aa'}15`
                      }}>
                        {getTypeIcon(type.slug, type.color)}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {type.name}
                      </Typography>
                      {type.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {type.description}
                        </Typography>
                      )}
                      {formData.projectTypeId === type.id && (
                        <Chip 
                          label="Sélectionné" 
                          size="small" 
                          sx={{ 
                            mt: 2, 
                            bgcolor: type.color || '#00d4aa',
                            color: '#fff'
                          }} 
                        />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {projectTypes.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2 }}>
              <Typography color="text.secondary">Chargement des types de projets...</Typography>
            </Box>
          )}
        </Stack>
      );
    }

    // === STEP: ENTREPRISE (Admin only) ===
    if (stepName === 'Entreprise') {
      return (
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Building2 color="#00d4aa" size={24} />
            <Typography variant="h6">Attribution du devis</Typography>
          </Box>

          {/* Toggle pour attribuer plus tard */}
          <Box 
            sx={{ 
              p: 2, 
              borderRadius: 2, 
              border: formData.assignLater ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.1)',
              bgcolor: formData.assignLater ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData.assignLater}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    assignLater: e.target.checked,
                    companyId: e.target.checked ? '' : formData.companyId,
                    clientId: e.target.checked ? '' : formData.clientId
                  })}
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Clock size={18} color={formData.assignLater ? '#f59e0b' : '#888'} />
                  <Typography sx={{ fontWeight: formData.assignLater ? 600 : 400 }}>
                    Attribuer plus tard
                  </Typography>
                </Box>
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 5.5 }}>
              Créer le devis sans l'attribuer à un client. Vous pourrez l'assigner ultérieurement.
            </Typography>
          </Box>

          {formData.assignLater && (
            <Alert severity="info" sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              Le devis sera créé sans attribution client. Vous pourrez l'attribuer depuis la page de détail du devis.
            </Alert>
          )}

          {/* Sélection entreprise (masquée si assignLater) */}
          {!formData.assignLater && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sélectionnez l'entreprise cliente pour laquelle vous créez ce devis.
              </Typography>
              
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.name || ''}
                value={companies.find(c => c.id === formData.companyId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, companyId: newValue?.id || '', clientId: '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Entreprise"
                    placeholder="Rechercher une entreprise..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search size={18} style={{ marginLeft: 8, marginRight: 4, opacity: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Building2 size={16} color="#00d4aa" />
                      <Typography variant="body1">{option.name}</Typography>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="Aucune entreprise trouvée"
              />

              {/* Sélection optionnelle du contact */}
              {formData.companyId && companyUsers.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <User color="#3b82f6" size={20} />
                    <Typography variant="subtitle2" color="text.secondary">Contact principal (optionnel)</Typography>
                  </Box>
                  <FormControl fullWidth size="small">
                    <InputLabel id="user-select-label">Utilisateur</InputLabel>
                    <Select
                      labelId="user-select-label"
                      value={formData.clientId}
                      label="Utilisateur"
                      onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    >
                      {companyUsers.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.firstName} {u.lastName} {u.role === 'CLIENTADMIN' ? '(Admin)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {formData.companyId && companyUsers.length === 0 && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                  Attention : Cette entreprise n'a pas encore d'utilisateurs. Le devis sera créé pour l'entreprise.
                </Typography>
              )}
            </>
          )}
        </Stack>
      );
    }

    // === STEP: DESCRIPTION ===
    if (stepName === 'Description') {
      return (
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FileText color="#00d4aa" size={24} />
            <Typography variant="h6">Détails du projet</Typography>
          </Box>
          <TextField
            fullWidth
            label="Titre du projet"
            placeholder="Ex: Refonte site e-commerce"
            variant="outlined"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description détaillée"
            placeholder="Décrivez vos besoins, objectifs et fonctionnalités clés..."
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </Stack>
      );
    }

    // === STEP: BUDGET (retiré - calculé automatiquement) ===
    if (false && stepName === 'Budget') {
      return (
          <Box sx={{ px: 2 }}></Box>
        );
    }

    // === STEP: TIMELINE ===
    if (stepName === 'Timeline') {
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Calendar color="#00d4aa" size={24} />
            <Typography variant="h6">Période souhaitée</Typography>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de début"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                helperText="Quand souhaiteriez-vous débuter ?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de fin (cible)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                helperText="Date de livraison souhaitée"
              />
            </Grid>
          </Grid>
        </Box>
      );
    }

    // === STEP: RÉCAPITULATIF ===
    if (stepName === 'Récapitulatif') {
      return (
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CheckCircle color="#00d4aa" size={24} />
            <Typography variant="h6">Récapitulatif</Typography>
          </Box>
          <Card sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            {/* Type de projet */}
            <Typography variant="subtitle2" color="primary" gutterBottom>TYPE DE PROJET</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              {selectedProjectType && getTypeIcon(selectedProjectType.slug, selectedProjectType.color)}
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedProjectType?.name || 'Non sélectionné'}
              </Typography>
            </Box>

            {isAdmin && (
              <>
                <Typography variant="subtitle2" color="primary" gutterBottom>ATTRIBUTION</Typography>
                {formData.assignLater ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip 
                      icon={<Clock size={14} />}
                      label="À attribuer plus tard" 
                      size="small"
                      sx={{ bgcolor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', borderColor: '#f59e0b' }}
                      variant="outlined"
                    />
                  </Box>
                ) : (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedCompany?.name || 'Non sélectionnée'}
                    </Typography>
                    {formData.clientId && (
                      <>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>CONTACT</Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {companyUsers.find(u => u.id === formData.clientId)?.firstName} {companyUsers.find(u => u.id === formData.clientId)?.lastName}
                        </Typography>
                      </>
                    )}
                  </>
                )}
              </>
            )}
            
            <Typography variant="subtitle2" color="primary" gutterBottom>PROJET</Typography>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>{formData.title || 'Sans titre'}</Typography>
            
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>TIMELINE</Typography>
            <Typography variant="body1">Du {formData.startDate || 'Non défini'} au {formData.endDate || 'Non défini'}</Typography>
          </Card>
        </Stack>
      );
    }

    return 'Unknown step';
  };

  const isNextDisabled = () => {
    const stepName = steps[activeStep];
    if (stepName === 'Type' && !formData.projectTypeId) return true;
    // Permettre de continuer si assignLater est activé OU si une entreprise est sélectionnée
    if (stepName === 'Entreprise' && isAdmin && !formData.assignLater && !formData.companyId) return true;
    if (stepName === 'Description' && !formData.title) return true;
    return false;
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4 }}>Nouvelle Soumission</Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 5, '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' }, '& .MuiStepIcon-root.Mui-completed': { color: 'primary.main' } }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card sx={{ p: { xs: 3, md: 6 }, backgroundColor: 'background.paper', backgroundImage: 'none', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: 4 }}>
        <Box sx={{ minHeight: '300px' }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} color="inherit" sx={{ px: 4 }}>
            Retour
          </Button>
          <Button 
            variant="contained" 
            onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
            disabled={isNextDisabled()}
            sx={{ px: 4, fontWeight: 'bold' }}
          >
            {activeStep === steps.length - 1 ? 'Confirmer l\'envoi' : 'Suivant'}
          </Button>
        </Box>
      </Card>
    </Container>
  );
}

export default NewSubmission;
